// Typed endpoint helpers over the envelope-unwrapping axios client.
// Shapes mirror DESIGN.md's route table; joined/aggregate responses that the
// shared package doesn't model (pipeline, stats, admin status) are typed here.
import type {
  Application,
  ApplicationNote,
  Candidate,
  DispositionOutboxItem,
  Job,
  OutboxStatus,
  PipelineStageColumn,
  StageEvent,
  SyncRun,
  User,
} from '@tipstrack/shared';
import { api, apiGet, apiPatch, apiPost } from './client.js';

// ---- Auth -------------------------------------------------------------------

export interface LoginResult {
  token: string;
  user: User;
}

export function login(email: string, password: string): Promise<LoginResult> {
  return apiPost<LoginResult>('/auth/login', { email, password });
}

export function fetchCurrentUser(): Promise<User> {
  return apiGet<User>('/auth/me');
}

// ---- Jobs -------------------------------------------------------------------

export interface JobInput {
  title: string;
  descriptionHtml: string;
  country: string;
  cityRegionPostal: string;
  contactEmail: string;
  contactPhone?: string | null;
  applyUrl?: string | null;
  salaryCurrency?: string | null;
  salaryPeriod?: string | null;
  salaryMinMinor?: number | null;
  benefits?: string[];
}

export function listJobs(): Promise<Job[]> {
  return apiGet<Job[]>('/jobs');
}

export function getJob(id: string): Promise<Job> {
  return apiGet<Job>(`/jobs/${id}`);
}

export function createJob(input: JobInput): Promise<Job> {
  return apiPost<Job>('/jobs', input);
}

export function updateJob(id: string, input: Partial<JobInput>): Promise<Job> {
  return apiPatch<Job>(`/jobs/${id}`, input);
}

export function publishJob(id: string): Promise<Job> {
  return apiPost<Job>(`/jobs/${id}/publish`);
}

export function expireJob(id: string): Promise<Job> {
  return apiPost<Job>(`/jobs/${id}/expire`);
}

// ---- Candidates -------------------------------------------------------------

export interface CandidateInput {
  fullName: string;
  email?: string;
  phone?: string;
  /** When set the API also creates an application on that job (stage 'new'). */
  jobId?: string;
}

export interface CandidateDetail extends Candidate {
  applications: Application[];
}

export function listCandidates(q?: string): Promise<Candidate[]> {
  return apiGet<Candidate[]>('/candidates', { params: q ? { q } : undefined });
}

export function getCandidate(id: string): Promise<CandidateDetail> {
  return apiGet<CandidateDetail>(`/candidates/${id}`);
}

export function createCandidate(input: CandidateInput): Promise<Candidate> {
  return apiPost<Candidate>('/candidates', input);
}

// ---- Applications -----------------------------------------------------------

export interface ApplicationDetail extends Application {
  candidate: Candidate;
  jobTitle: string;
  stageEvents: StageEvent[];
  notes: ApplicationNote[];
  outbox: DispositionOutboxItem[];
}

export function listApplications(params: { jobId?: string; stage?: string } = {}): Promise<Application[]> {
  return apiGet<Application[]>('/applications', { params });
}

export function getApplication(id: string): Promise<ApplicationDetail> {
  return apiGet<ApplicationDetail>(`/applications/${id}`);
}

export function updateApplicationStage(
  id: string,
  stageKey: string,
  note?: string,
): Promise<Application> {
  return apiPatch<Application>(`/applications/${id}/stage`, { stageKey, note: note || undefined });
}

export function addApplicationNote(id: string, body: string): Promise<ApplicationNote> {
  return apiPost<ApplicationNote>(`/applications/${id}/notes`, { body });
}

/**
 * Download the resume bytes with the bearer attached (a bare <a href> would not
 * carry Authorization), then trigger a client-side save.
 */
export async function downloadResume(applicationId: string, fallbackName: string): Promise<void> {
  const res = await api.get<Blob>(`/applications/${applicationId}/resume`, {
    responseType: 'blob',
  });
  const disposition = String(res.headers['content-disposition'] ?? '');
  const match = /filename\*?="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Pipeline ---------------------------------------------------------------

export interface PipelineResponse {
  stages: PipelineStageColumn[];
}

export interface PipelineStats {
  funnel: Array<{ stageKey: string; label: string; count: number }>;
  totals?: {
    jobs?: number;
    openJobs?: number;
    candidates?: number;
    applications?: number;
  };
  recentApplications?: Application[];
}

export function fetchPipeline(jobId: string): Promise<PipelineResponse> {
  return apiGet<PipelineResponse>('/pipeline', { params: { jobId } });
}

export function fetchPipelineStats(): Promise<PipelineStats> {
  return apiGet<PipelineStats>('/pipeline/stats');
}

// ---- Admin ------------------------------------------------------------------

export interface IndeedStatus {
  mode: 'mock' | 'live';
  jobsSynced: number;
  pendingOutbox: number;
  lastRuns: SyncRun[] | Partial<Record<string, SyncRun>>;
}

/** Normalize lastRuns (array or by-kind record) into a flat array for rendering. */
export function lastRunsList(status: IndeedStatus | undefined): SyncRun[] {
  if (!status?.lastRuns) return [];
  if (Array.isArray(status.lastRuns)) return status.lastRuns;
  return Object.values(status.lastRuns).filter((r): r is SyncRun => Boolean(r));
}

export function fetchIndeedStatus(): Promise<IndeedStatus> {
  return apiGet<IndeedStatus>('/admin/indeed/status');
}

export function listSyncRuns(): Promise<SyncRun[]> {
  return apiGet<SyncRun[]>('/admin/sync-runs');
}

export function listOutbox(status?: OutboxStatus | ''): Promise<DispositionOutboxItem[]> {
  return apiGet<DispositionOutboxItem[]>('/admin/outbox', {
    params: status ? { status } : undefined,
  });
}

export function retryOutboxItem(id: number): Promise<DispositionOutboxItem> {
  return apiPost<DispositionOutboxItem>(`/admin/outbox/${id}/retry`);
}

export interface MockApplyInput {
  jobId: string;
  fullName: string;
  email: string;
  phone?: string;
  resumeText?: string;
}

export function mockApply(input: MockApplyInput): Promise<unknown> {
  return apiPost<unknown>('/admin/mock/apply', input);
}
